import { LOCALE, t } from './i18n.ts'
/**
 * Pure helpers for the unified calendar (issue #11). Dates are `YYYY-MM-DD` in the condo's zone; no Date arithmetic in
 * local time. Since ADR 0035 the events come ready from `GET /condos/:id/agenda` (days and times already in the
 * condo's zone); the helpers here lay them out by month.
 */
export type AgendaKind = 'reserva' | 'assembleia' | 'prazo' | 'mudanca' | 'prestador' | 'reforma'

export interface AgendaEvent {
  id: string
  kind: AgendaKind
  /** First day (inclusive). */
  date: string
  /** Last day (inclusive) for multi-day items; omitted for one-day items. */
  endDate?: string
  /** `HH:MM` when the item has a time of day. */
  time?: string
  endTime?: string
  title: string
  subtitle?: string
  location?: string
}

export const KIND_LABEL: Record<AgendaKind, string> = { reserva: t('Reserva'), assembleia: t('Assembleia'), prazo: t('Prazo'), mudanca: t('Mudança'), prestador: t('Prestador'), reforma: t('Reforma') }

const TZ = 'America/Sao_Paulo'
const ymd = new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' })
const hm = new Intl.DateTimeFormat(LOCALE, { timeZone: TZ, hour: '2-digit', minute: '2-digit' })

/** ISO instant → `YYYY-MM-DD` in the condo's zone. */
export const localDate = (iso: string | Date): string => ymd.format(new Date(iso))
/** ISO instant → `HH:MM` in the condo's zone. */
export const localTime = (iso: string | Date): string => hm.format(new Date(iso))

export const pad2 = (n: number) => String(n).padStart(2, '0')
export const monthKey = (y: number, m: number) => `${y}-${pad2(m)}`
/** `{y, m}` plus n months (m is 1–12). */
export function addMonths(y: number, m: number, n: number): { y: number; m: number } {
  const idx = y * 12 + (m - 1) + n
  return { y: Math.floor(idx / 12), m: (idx % 12) + 1 }
}
export const daysInMonth = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate()
/** 0 = Sunday … 6 = Saturday for a `YYYY-MM-DD` (calendar arithmetic only). */
export const weekday = (date: string) => new Date(`${date}T00:00:00Z`).getUTCDay()
export const plusDays = (date: string, n: number) => new Date(Date.parse(`${date}T00:00:00Z`) + n * 86400_000).toISOString().slice(0, 10)

/** The six-week grid (42 cells) that shows a month, starting on Sunday; cells outside the month are null. */
export function monthGrid(y: number, m: number): (string | null)[] {
  const first = `${monthKey(y, m)}-01`
  const lead = weekday(first)
  const n = daysInMonth(y, m)
  const cells: (string | null)[] = []
  for (let i = 0; i < 42; i++) {
    const d = i - lead + 1
    cells.push(d >= 1 && d <= n ? `${monthKey(y, m)}-${pad2(d)}` : null)
  }
  return cells
}

/** Events that touch a day (one-day items on that day, ranges that contain it). */
export const eventsOn = (events: AgendaEvent[], date: string): AgendaEvent[] =>
  events.filter((e) => e.date <= date && (e.endDate ?? e.date) >= date).sort((a, b) => (a.time ?? '').localeCompare(b.time ?? '') || a.title.localeCompare(b.title))

/** Events that touch a month, for the badge counts. */
export const eventsInMonth = (events: AgendaEvent[], y: number, m: number): AgendaEvent[] => {
  const from = `${monthKey(y, m)}-01`
  const to = `${monthKey(y, m)}-${pad2(daysInMonth(y, m))}`
  return events.filter((e) => e.date <= to && (e.endDate ?? e.date) >= from)
}

/** Short weekday names from Sunday, in the reader's language (2026-10-18 was a Sunday). */
export const WEEKDAYS = Array.from({ length: 7 }, (_, i) => new Intl.DateTimeFormat(LOCALE, { weekday: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(2026, 9, 18 + i))).replace('.', ''))
export const MONTHS = Array.from({ length: 12 }, (_, i) => new Intl.DateTimeFormat(LOCALE, { month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2026, i, 15))))
